import { CheckableField, CheckableFields, FormField, Heading, TextInput } from '@msaf/core-react'
import { WellGeothermalFieldsEdit } from '../../../../types/form-types'
import { SetValue, ValidationErrors } from '../../../hooks/use-edit-form'

interface WellConditionInterface {
  fields: Partial<WellGeothermalFieldsEdit>
  isLoading: boolean
  setValue: SetValue<WellGeothermalFieldsEdit>
  validationErrors: ValidationErrors
}

export default function WellConditionEdit({ fields, isLoading, validationErrors, setValue }: WellConditionInterface) {
  return (
    <>
      <Heading isSkeleton={isLoading} level={3}>
        Condition of well
      </Heading>
      <FormField
        validationMessages={validationErrors?.hasExternalCorrosionLeakage}
        isSkeleton={isLoading}
        label='External corrosion leakage'
        labelId='hasExternalCorrosionLeakageLabel'
      >
        <CheckableFields ariaLabelledBy='hasExternalCorrosionLeakageLabel' isHorizontal>
          <CheckableField
            id='hasExternalCorrosionLeakageYes'
            name='hasExternalCorrosionLeakage'
            describedBy='hasExternalCorrosionLeakage'
            label='Yes'
            isSkeleton={isLoading}
            type='radio'
            checked={fields.hasExternalCorrosionLeakage ?? false}
            onChange={() => setValue('hasExternalCorrosionLeakage', true)}
          />
          <CheckableField
            id='hasExternalCorrosionLeakageNo'
            name='hasExternalCorrosionLeakage'
            describedBy='hasExternalCorrosionLeakage'
            label='No'
            isSkeleton={isLoading}
            type='radio'
            checked={fields.hasExternalCorrosionLeakage === false}
            onChange={() => setValue('hasExternalCorrosionLeakage', false)}
          />
        </CheckableFields>
      </FormField>
      <FormField
        isSkeleton={isLoading}
        label='External corrosion leakage details'
        htmlFor='externalCorrosionLeakageDetails'
        validationMessages={validationErrors?.externalCorrosionLeakageDetails}
      >
        <TextInput
          isSkeleton={isLoading}
          type='text'
          id='externalCorrosionLeakageDetails'
          value={`${fields.externalCorrosionLeakageDetails ?? ''}`}
          setValue={(value) => setValue('externalCorrosionLeakageDetails', value)}
        />
      </FormField>
      <FormField
        validationMessages={validationErrors?.hasWellheadCorrosionLeakage}
        isSkeleton={isLoading}
        label='Wellhead corrosion leakage'
        labelId='hasWellheadCorrosionLeakageLabel'
      >
        <CheckableFields ariaLabelledBy='hasWellheadCorrosionLeakageLabel' isHorizontal>
          <CheckableField
            id='hasWellheadCorrosionLeakageYes'
            name='hasWellheadCorrosionLeakage'
            describedBy='hasWellheadCorrosionLeakage'
            label='Yes'
            isSkeleton={isLoading}
            type='radio'
            checked={fields.hasWellheadCorrosionLeakage ?? false}
            onChange={() => setValue('hasWellheadCorrosionLeakage', true)}
          />
          <CheckableField
            id='hasWellheadCorrosionLeakageNo'
            name='hasWellheadCorrosionLeakage'
            describedBy='hasWellheadCorrosionLeakage'
            label='No'
            isSkeleton={isLoading}
            type='radio'
            checked={fields.hasWellheadCorrosionLeakage === false}
            onChange={() => setValue('hasWellheadCorrosionLeakage', false)}
          />
        </CheckableFields>
      </FormField>
      <FormField
        isSkeleton={isLoading}
        label='Wellhead corrosion leakage details'
        htmlFor='wellheadCorrosionLeakageDetails'
        validationMessages={validationErrors?.wellheadCorrosionLeakageDetails}
      >
        <TextInput
          isSkeleton={isLoading}
          type='text'
          id='wellheadCorrosionLeakageDetails'
          value={`${fields.wellheadCorrosionLeakageDetails ?? ''}`}
          setValue={(value) => setValue('wellheadCorrosionLeakageDetails', value)}
        />
      </FormField>
      <FormField
        validationMessages={validationErrors?.hasBrokenOrPerforatedCasing}
        isSkeleton={isLoading}
        label='Broken or perforated casing'
        labelId='hasBrokenOrPerforatedCasingLabel'
      >
        <CheckableFields ariaLabelledBy='hasBrokenOrPerforatedCasingLabel' isHorizontal>
          <CheckableField
            id='hasBrokenOrPerforatedCasingYes'
            name='hasBrokenOrPerforatedCasing'
            describedBy='hasBrokenOrPerforatedCasing'
            label='Yes'
            isSkeleton={isLoading}
            type='radio'
            checked={fields.hasBrokenOrPerforatedCasing ?? false}
            onChange={() => setValue('hasBrokenOrPerforatedCasing', true)}
          />
          <CheckableField
            id='hasBrokenOrPerforatedCasingNo'
            name='hasBrokenOrPerforatedCasing'
            describedBy='hasBrokenOrPerforatedCasing'
            label='No'
            isSkeleton={isLoading}
            type='radio'
            checked={fields.hasBrokenOrPerforatedCasing === false}
            onChange={() => setValue('hasBrokenOrPerforatedCasing', false)}
          />
        </CheckableFields>
      </FormField>
      <FormField
        isSkeleton={isLoading}
        label='Broken or perforated casing details'
        htmlFor='brokenOrPerforatedCasingDetails'
        validationMessages={validationErrors?.brokenOrPerforatedCasingDetails}
      >
        <TextInput
          isSkeleton={isLoading}
          type='text'
          id='brokenOrPerforatedCasingDetails'
          value={`${fields.brokenOrPerforatedCasingDetails ?? ''}`}
          setValue={(value) => setValue('brokenOrPerforatedCasingDetails', value)}
        />
      </FormField>
      <FormField
        validationMessages={validationErrors?.hasFailedCasingConnections}
        isSkeleton={isLoading}
        label='Failed casing connections'
        labelId='hasFailedCasingConnectionsLabel'
      >
        <CheckableFields ariaLabelledBy='hasFailedCasingConnectionsLabel' isHorizontal>
          <CheckableField
            id='hasFailedCasingConnectionsYes'
            name='hasFailedCasingConnections'
            describedBy='hasFailedCasingConnections'
            label='Yes'
            isSkeleton={isLoading}
            type='radio'
            checked={fields.hasFailedCasingConnections ?? false}
            onChange={() => setValue('hasFailedCasingConnections', true)}
          />
          <CheckableField
            id='hasFailedCasingConnectionsNo'
            name='hasFailedCasingConnections'
            describedBy='hasFailedCasingConnections'
            label='No'
            isSkeleton={isLoading}
            type='radio'
            checked={fields.hasFailedCasingConnections === false}
            onChange={() => setValue('hasFailedCasingConnections', false)}
          />
        </CheckableFields>
      </FormField>
      <FormField
        isSkeleton={isLoading}
        label='Failed casing connections details'
        htmlFor='failedCasingConnectionsDetails'
        validationMessages={validationErrors?.failedCasingConnectionsDetails}
      >
        <TextInput
          isSkeleton={isLoading}
          type='text'
          id='failedCasingConnectionsDetails'
          value={`${fields.failedCasingConnectionsDetails ?? ''}`}
          setValue={(value) => setValue('failedCasingConnectionsDetails', value)}
        />
      </FormField>
      <FormField
        validationMessages={validationErrors?.hasLeaksIntoOutOfCasing}
        isSkeleton={isLoading}
        label='Leaks from casing'
        labelId='hasLeaksIntoOutOfCasingLabel'
      >
        <CheckableFields ariaLabelledBy='hasLeaksIntoOutOfCasingLabel' isHorizontal>
          <CheckableField
            id='hasLeaksIntoOutOfCasingYes'
            name='hasLeaksIntoOutOfCasing'
            describedBy='hasLeaksIntoOutOfCasing'
            label='Yes'
            isSkeleton={isLoading}
            type='radio'
            checked={fields.hasLeaksIntoOutOfCasing ?? false}
            onChange={() => setValue('hasLeaksIntoOutOfCasing', true)}
          />
          <CheckableField
            id='hasLeaksIntoOutOfCasingNo'
            name='hasLeaksIntoOutOfCasing'
            describedBy='hasLeaksIntoOutOfCasing'
            label='No'
            isSkeleton={isLoading}
            type='radio'
            checked={fields.hasLeaksIntoOutOfCasing === false}
            onChange={() => setValue('hasLeaksIntoOutOfCasing', false)}
          />
        </CheckableFields>
      </FormField>
      <FormField
        isSkeleton={isLoading}
        label='Leaks from casing details'
        htmlFor='leaksIntoOutOfCasingDetails'
        validationMessages={validationErrors?.leaksIntoOutOfCasingDetails}
      >
        <TextInput
          isSkeleton={isLoading}
          type='text'
          id='leaksIntoOutOfCasingDetails'
          value={`${fields.leaksIntoOutOfCasingDetails ?? ''}`}
          setValue={(value) => setValue('leaksIntoOutOfCasingDetails', value)}
        />
      </FormField>
      <FormField
        validationMessages={validationErrors?.hasBuckledDistortedCasing}
        isSkeleton={isLoading}
        label='Buckled distorted casing'
        labelId='hasBuckledDistortedCasingLabel'
      >
        <CheckableFields ariaLabelledBy='hasBuckledDistortedCasingLabel' isHorizontal>
          <CheckableField
            id='hasBuckledDistortedCasingYes'
            name='hasBuckledDistortedCasing'
            describedBy='hasBuckledDistortedCasing'
            label='Yes'
            isSkeleton={isLoading}
            type='radio'
            checked={fields.hasBuckledDistortedCasing ?? false}
            onChange={() => setValue('hasBuckledDistortedCasing', true)}
          />
          <CheckableField
            id='hasBuckledDistortedCasingNo'
            name='hasBuckledDistortedCasing'
            describedBy='hasBuckledDistortedCasing'
            label='No'
            isSkeleton={isLoading}
            type='radio'
            checked={fields.hasBuckledDistortedCasing === false}
            onChange={() => setValue('hasBuckledDistortedCasing', false)}
          />
        </CheckableFields>
      </FormField>
      <FormField
        isSkeleton={isLoading}
        label='Buckled distorted casing details'
        htmlFor='buckledDistortedCasingDetails'
        validationMessages={validationErrors?.buckledDistortedCasingDetails}
      >
        <TextInput
          isSkeleton={isLoading}
          type='text'
          id='buckledDistortedCasingDetails'
          value={`${fields.buckledDistortedCasingDetails ?? ''}`}
          setValue={(value) => setValue('buckledDistortedCasingDetails', value)}
        />
      </FormField>
      <FormField
        validationMessages={validationErrors?.hasCorrodedCasing}
        isSkeleton={isLoading}
        label='Corroded casing'
        labelId='hasCorrodedCasingLabel'
      >
        <CheckableFields ariaLabelledBy='hasCorrodedCasingLabel' isHorizontal>
          <CheckableField
            id='hasCorrodedCasingYes'
            name='hasCorrodedCasing'
            describedBy='hasCorrodedCasing'
            label='Yes'
            isSkeleton={isLoading}
            type='radio'
            checked={fields.hasCorrodedCasing ?? false}
            onChange={() => setValue('hasCorrodedCasing', true)}
          />
          <CheckableField
            id='hasCorrodedCasingNo'
            name='hasCorrodedCasing'
            describedBy='hasCorrodedCasing'
            label='No'
            isSkeleton={isLoading}
            type='radio'
            checked={fields.hasCorrodedCasing === false}
            onChange={() => setValue('hasCorrodedCasing', false)}
          />
        </CheckableFields>
      </FormField>
      <FormField
        isSkeleton={isLoading}
        label='Corroded casing details'
        htmlFor='corrodedCasingDetails'
        validationMessages={validationErrors?.corrodedCasingDetails}
      >
        <TextInput
          isSkeleton={isLoading}
          type='text'
          id='corrodedCasingDetails'
          value={`${fields.corrodedCasingDetails ?? ''}`}
          setValue={(value) => setValue('corrodedCasingDetails', value)}
        />
      </FormField>
    </>
  )
}
